import { FC } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select as MuiSelect,
    SelectProps as MuiSelectProps,
    InputAdornment,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface FormSelectProps extends Omit<MuiSelectProps, 'name'> {
    name: string;
    label: string;
    options: SelectOption[];
    placeholder?: string;
    helperText?: string;
    showSuccessIcon?: boolean;
    showErrorIcon?: boolean;
}

export const FormSelect: FC<FormSelectProps> = ({
    name,
    label,
    options,
    placeholder,
    helperText,
    required = false,
    disabled = false,
    showSuccessIcon = true,
    showErrorIcon = true,
    ...rest
}) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const error = errors[name];
    const hasError = !!error;
    const errorMessage = error?.message as string;

    const isValid = !hasError && control._formState.touchedFields[name];

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <FormControl fullWidth error={hasError} variant="outlined">
                    <InputLabel required={required}>{label}</InputLabel>
                    <MuiSelect
                        {...field}
                        {...rest}
                        label={label}
                        disabled={disabled}
                        displayEmpty={!!placeholder}
                        endAdornment={
                            <InputAdornment position="end" sx={{ mr: 3 }}>
                                {showErrorIcon && hasError && (
                                    <ErrorIcon color="error" fontSize="small" />
                                )}
                                {showSuccessIcon && isValid && (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                )}
                            </InputAdornment>
                        }
                        sx={{
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                            },
                            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                            },
                        }}
                    >
                        {placeholder && (
                            <MenuItem value="" disabled>
                                <em>{placeholder}</em>
                            </MenuItem>
                        )}
                        {options.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </MuiSelect>
                    {(hasError || helperText) && (
                        <FormHelperText>{hasError ? errorMessage : helperText}</FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
};

